import { useEffect, useRef, useState } from "react";

import { claudeAgentApi } from "@/lib/api/claude-agent";

// Thumbnails live outside `public/` (in `src/data/.slide-thumbnails/`) and are
// served by the sidecar — not the user's Vite dev server. The filename is
// derived deterministically from slide.id: `t-{slide.id}.jpg`. Each capture
// overwrites the same file; cache safety comes from ETag/Last-Modified
// (sidecar reads file mtime, browser revalidates on revision bump).
export type ThumbnailState = {
  url: string | null;
  error: boolean;
  loading: boolean;
};

// Thumbnail filenames now follow the clean layout `t-{slide.id}.jpg`. The
// frontend no longer reads `slide.thumbnail` from the manifest — the hook
// resolves the file via the sidecar's by-slide-id endpoint, which reads
// `t-{slideId}.jpg` directly (with a legacy fallback). The bug where
// `slide.thumbnail` gets dropped on manifest rewrite no longer affects the
// UI.
export const SLIDE_ID_RE = /^[a-zA-Z0-9_-]+$/;

export function useSlideThumbnail(
  slideId: string | undefined,
  devContainerId: string | undefined,
  getIdToken: () => Promise<string | null>,
  revision: number,
): ThumbnailState {
  const hasValidId = Boolean(slideId && SLIDE_ID_RE.test(slideId));

  const [state, setState] = useState<ThumbnailState>({
    url: null,
    error: false,
    loading: Boolean(devContainerId && hasValidId),
  });
  const currentUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!devContainerId || !slideId || !hasValidId) {
      setState({ url: null, error: false, loading: Boolean(devContainerId) });
      return;
    }

    const controller = new AbortController();
    let cancelled = false;

    setState((prev) => ({ url: prev.url, error: false, loading: true }));

    (async () => {
      try {
        const token = await getIdToken();
        if (!token || cancelled) return;
        // by-slide-id endpoint reads `t-{slideId}.jpg` directly (with legacy
        // fallback). URL is stable per slide; the server sends
        // `Cache-Control: no-cache` + ETag for revalidation. We also pass
        // `revision` as `?v={n}` so the URL changes on every
        // slides-thumbnails-updated bump — that forces a fresh network round
        // trip even on browsers that try to serve the disk-cached body
        // without revalidating.
        const blob = await claudeAgentApi.fetchSlideThumbnailBySlideId(
          token,
          devContainerId,
          slideId,
          controller.signal,
          revision,
        );
        if (cancelled) return;
        const next = URL.createObjectURL(blob);
        if (currentUrlRef.current) {
          URL.revokeObjectURL(currentUrlRef.current);
        }
        currentUrlRef.current = next;
        setState({ url: next, error: false, loading: false });
      } catch (err) {
        if (cancelled || (err as Error).name === "AbortError") return;
        setState({ url: null, error: true, loading: false });
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [slideId, hasValidId, devContainerId, getIdToken, revision]);

  // Final cleanup on unmount — revoke any blob URL still held.
  useEffect(() => {
    return () => {
      if (currentUrlRef.current) {
        URL.revokeObjectURL(currentUrlRef.current);
        currentUrlRef.current = null;
      }
    };
  }, []);

  return state;
}
